/**
 * IAB TCF CMP API stub + lightweight SDK / Consent Mode defaults.
 *
 * IMPORTANT: `@iabtechlabtcf/stub` only auto-invokes when loaded as a
 * classic script (`typeof module === "undefined" ? stub() : module.exports = stub`).
 * esbuild's CJS interop defines `module`, so a bare
 * `import "@iabtechlabtcf/stub"` is a no-op in our IIFE bundle — which
 * means `window.__tcfapi` and the `__tcfapiLocator` iframe never appear.
 * The IAB Europe CMP Validator detects a CMP solely via
 * `document.getElementsByName("__tcfapiLocator")[0]`, so a silent stub
 * makes the validator report "No CMP API found" even while our banner UI
 * is visible. Always invoke the exported factory after import.
 */
import installTcfApiStub from "@iabtechlabtcf/stub";
import { SDK_NAME } from "./constants";

// Never install a second stub over an existing one: when stub.js runs in <head>
// the locator iframe is only appended once <body> exists (the IAB stub polls
// every 5 ms). If the async cmp.js bundle executes in that window, a naive
// second installTcfApiStub() finds no __tcfapiLocator frame, replaces
// window.__tcfapi with a fresh, empty queue and silently drops every call
// queued so far (e.g. an ad tag's early addEventListener — the listener would
// never receive cmpuishown/useractioncomplete).
if (typeof installTcfApiStub === "function" && typeof (window as unknown as { __tcfapi?: unknown }).__tcfapi !== "function") {
  installTcfApiStub();
}

/**
 * Lightweight stub intended for inline head snippet (also imported by bundle).
 * Sets up command queue + Consent Mode default denied until the full CMP loads.
 */
(function stub() {
  const w = window as unknown as Record<string, unknown> & { dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void };
  w[SDK_NAME] = w[SDK_NAME] || {
    q: [],
    consentAll(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["consentAll", ...args]); },
    declineAll(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["declineAll", ...args]); },
    show(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["show", ...args]); },
    on(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["on", ...args]); },
    off(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["off", ...args]); },
    setLanguage(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["setLanguage", ...args]); },
    console(...args: unknown[]) { (this as { q: unknown[][] }).q.push(["console", ...args]); },
    getConsent() { return null; },
    hasConsent() { return false; }
  };

  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag(...args: unknown[]) { w.dataLayer!.push(args); };
  }
  w.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500
  });
})();
